import { z } from 'zod';

import { NONE_VALUE } from '@/constants/answers';
import { mainstreamCountriesQueryOptions } from '@/hooks/queries/use-mainstream-countries';
import i18n from '@/lib/i18n';
import { queryClient } from '@/lib/query-client';
import { fastApi } from '@/services/api';
import { mainstreamCountrySchema } from '@/services/api/mainstream/country';
import { getNationalClientIdentifierFormatError } from '@/services/api/profile/national-client-identifier-format';
import { NationalClientIdentifierJustificationReason, NationalClientIdentifierType } from '@/utils/national-client-identifier';

const PATH = 'v1/customer/form/national-client-identifier';

const priorityJustificationSchema = z
  .union([z.enum(NationalClientIdentifierJustificationReason, { error: i18n.t('requiredField.error') }), z.literal('')])
  .transform((value) => (value === '' ? undefined : value))
  .optional();

export const nationalClientIdentifierAnswersSchema = z
  .object({
    country: mainstreamCountrySchema(),
    identifierType: z.enum([...Object.values(NationalClientIdentifierType), NONE_VALUE], {
      error: i18n.t('requiredField.error'),
    }),
    identifier: z
      .string()
      .transform((value) => (value === '' ? undefined : value))
      .optional(),
    firstPriorityJustification: priorityJustificationSchema,
    secondPriorityJustification: priorityJustificationSchema,
  })
  .superRefine(async (data, ctx) => {
    const countries = await queryClient.ensureQueryData(mainstreamCountriesQueryOptions);
    const country = countries?.[data.country];
    if (!country) return;

    const { identifierType, identifier, firstPriorityJustification, secondPriorityJustification } = data;
    if (identifierType === NONE_VALUE) {
      if (country.nciPriority.includes(NationalClientIdentifierType.CONCAT)) {
        ctx.addIssue({
          message: i18n.t('nciPrioritySelection.errors.identifierType.invalid'),
          path: ['identifierType'],
        });
      }
      return;
    }

    if (!country.nciPriority.includes(identifierType)) {
      ctx.addIssue({
        message: i18n.t('nciPrioritySelection.errors.identifierType.invalid'),
        path: ['identifierType'],
      });
      return;
    }

    if (identifierType !== NationalClientIdentifierType.CONCAT) {
      if (!identifier) {
        ctx.addIssue({
          message: i18n.t('requiredField.error'),
          path: ['identifier'],
        });
        return;
      }

      const formatError = await getNationalClientIdentifierFormatError({ country: data.country, identifier, identifierType });
      if (formatError) {
        ctx.addIssue({
          message: formatError,
          path: ['identifier'],
        });
        return;
      }
    }

    // NCI justification requirements depend on the selected NCI's priority position:
    // - priority 1 (index 0): no justifications needed
    // - priority 2 (index 1): firstPriorityJustification required
    // - priority 3 (index 2): firstPriorityJustification and secondPriorityJustification required
    const nciPriorityIndex = country.nciPriority.indexOf(identifierType);
    if (nciPriorityIndex === 0) return;
    if (nciPriorityIndex === 1 && !firstPriorityJustification) {
      ctx.addIssue({
        code: 'custom',
        message: i18n.t('requiredField.error'),
        path: ['firstPriorityJustification'],
      });
      return;
    }
    if (nciPriorityIndex === 2) {
      if (!firstPriorityJustification) {
        ctx.addIssue({
          code: 'custom',
          message: i18n.t('requiredField.error'),
          path: ['firstPriorityJustification'],
        });
      }

      if (!secondPriorityJustification) {
        ctx.addIssue({
          code: 'custom',
          message: i18n.t('requiredField.error'),
          path: ['secondPriorityJustification'],
        });
      }
    }
  });

export type NationalClientIdentifierAnswers = z.infer<typeof nationalClientIdentifierAnswersSchema>;

export const nationalClientIdentifierSchema = z.object({
  answers: nationalClientIdentifierAnswersSchema,
});

export type NationalClientIdentifier = z.infer<typeof nationalClientIdentifierSchema>;

type NationalClientIdentifierGetResponse = NationalClientIdentifier;

type NationalClientIdentifierPostResponse = NationalClientIdentifier;

export interface NationalClientIdentifierPostErrorResponse {
  error?: string;
}

export type NationalClientIdentifierPostRequestBody = NationalClientIdentifier;

export const getNationalClientIdentifier = async () => {
  return fastApi
    .get(PATH)
    .json<NationalClientIdentifierGetResponse>()
    .catch((error) => {
      if (error.response.status === 404) {
        return null;
      }
      throw error;
    });
};

export const postNationalClientIdentifier = async (data: NationalClientIdentifier) => {
  return fastApi.post(PATH, { json: data }).json<NationalClientIdentifierPostResponse>();
};
